import { SxProps, useTheme } from "@mui/material";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  ChartOptions,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { useEffect, useState } from "react";

ChartJS.register(LinearScale, CategoryScale, PointElement, LineElement, Title, Tooltip, Legend);

interface IProps {
  sx?: SxProps;
  lineCount?: number;
  pointCount?: number;
  getOptions?: () => ChartOptions<"line">;
}

const LineGraphSkeleton = ({ lineCount = 2, pointCount = 12, ...props }: IProps) => {
  const theme = useTheme();

  const generateRandomData = () => {
    return [...Array.from({ length: pointCount }, () => Math.random() * 6 - 3)];
  };

  let data = [...new Array(lineCount)].map(() => generateRandomData());

  const [chartData, setChartData] = useState({
    labels: Array.from({ length: pointCount }, (_, i) => i + 1), // Initial labels
    datasets: data.map((d) => {
      return {
        data: d,
        tension: 0.3,
        pointRadius: 0,
        borderColor: theme.palette.grey[200],
        borderWidth: 3,
      };
    }),
  });

  // Update the chart data every second
  useEffect(() => {
    const interval = setInterval(() => {
      setChartData((prevChartData) => {
        return {
          ...prevChartData,
          datasets: prevChartData.datasets.map((dataset) => {
            return {
              ...dataset,
              data: generateRandomData(),
            };
          }),
        };
      });
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getOptions(): ChartOptions<"line"> {
    return {
      responsive: true,
      maintainAspectRatio: false,
      elements: {
        point: {
          radius: 0,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      scales: {
        xAxis: {
          display: false,
        },
        yAxis: {
          display: false,
          ticks: {
            callback: () => {
              return "";
            },
          },
          max: 4,
          min: -4,
        },
      },
    };
  }

  return <Line options={props.getOptions ? props.getOptions() : getOptions()} data={chartData} />;
};

export { LineGraphSkeleton };
